import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SharedGroup {
  id: string;
  name: string;
  member_count: number;
  shared_at: string;
}

export function useSharedCasoGroups(casoId: string) {
  return useQuery<SharedGroup[]>({
    queryKey: ['shared-caso-groups', casoId],
    queryFn: async () => {
      // 1. Buscar compartilhamentos do caso
      const { data: shares, error: sharesError } = await supabase
        .from('caso_group_shares')
        .select('group_id, shared_at')
        .eq('caso_id', casoId);

      if (sharesError) throw sharesError;
      if (!shares || shares.length === 0) return [];

      // 2. Buscar informações dos grupos
      const groupIds = shares.map(s => s.group_id);
      const { data: groups, error: groupsError } = await supabase
        .from('sharing_groups')
        .select('id, name')
        .in('id', groupIds);

      if (groupsError) throw groupsError;

      // 3. Contar membros de cada grupo
      const { data: members, error: membersError } = await supabase
        .from('sharing_group_members')
        .select('group_id')
        .in('group_id', groupIds);

      if (membersError) throw membersError;

      // 4. Agregar dados
      const memberCountMap = members?.reduce((acc, m) => {
        acc[m.group_id] = (acc[m.group_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      return shares.map(share => {
        const group = groups?.find(g => g.id === share.group_id);
        return {
          id: share.group_id,
          name: group?.name || 'Grupo Desconhecido',
          member_count: memberCountMap[share.group_id] || 0,
          shared_at: share.shared_at,
        };
      });
    },
    enabled: !!casoId,
  });
}

export function useSharedConversationGroups(conversationId: string) {
  return useQuery<SharedGroup[]>({
    queryKey: ['shared-conversation-groups', conversationId],
    queryFn: async () => {
      // 1. Buscar compartilhamentos da conversa
      const { data: shares, error: sharesError } = await supabase
        .from('conversation_group_shares')
        .select('group_id, shared_at')
        .eq('conversation_id', conversationId);

      if (sharesError) throw sharesError;
      if (!shares || shares.length === 0) return [];

      // 2. Buscar informações dos grupos
      const groupIds = shares.map(s => s.group_id);
      const { data: groups, error: groupsError } = await supabase
        .from('sharing_groups')
        .select('id, name')
        .in('id', groupIds);

      if (groupsError) throw groupsError;

      // 3. Contar membros de cada grupo
      const { data: members, error: membersError } = await supabase
        .from('sharing_group_members')
        .select('group_id')
        .in('group_id', groupIds);

      if (membersError) throw membersError;

      // 4. Agregar dados
      const memberCountMap = members?.reduce((acc, m) => {
        acc[m.group_id] = (acc[m.group_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      return shares.map(share => {
        const group = groups?.find(g => g.id === share.group_id);
        return {
          id: share.group_id,
          name: group?.name || 'Grupo Desconhecido',
          member_count: memberCountMap[share.group_id] || 0,
          shared_at: share.shared_at,
        };
      });
    },
    enabled: !!conversationId,
  });
}
